import _ from 'lodash';

import { ensureArray } from '@zapier/common-utils';

import { selectErrorMessages } from './utils';

export default function createErrorType(name, options) {
  // A summary is the description of the error. No child error messages or detail.
  const errorSummary = error => {
    if (typeof options.summary === 'string') {
      return options.summary;
    }

    if (typeof options.summary === 'function') {
      return options.summary(error);
    }

    if (error.summary) {
      return error.summary;
    } // No summary here, sorry.

    return '';
  };

  // Child error messages of this error.
  const errorErrors = function (error) {
    if (typeof options.errors === 'function') {
      return options.errors(error);
    }

    if (_.isArray(error.errors)) {
      return error.errors;
    }

    if (error.responseText) {
      try {
        const response = JSON.parse(error.responseText);

        if (response.errors) {
          return ensureArray(response.errors);
        }

        // If no errors property, return an empty array, *not* undefined, so callers won't blow up on errors.length
        return [];
      } catch (e) {
        return [];
      }
    }

    return [];
  };

  // Try to make a good message for logging.
  const errorMessage = error => {
    const errors = errorErrors(error);
    const summary = errorSummary(error);

    return errors.length === 0
      ? summary
      : `${summary} (${selectErrorMessages(errors).join(', ')})`;
  };

  const hasFeature = (featureFlag, error) => {
    if (_.isFunction(options[featureFlag])) {
      return !!options[featureFlag](error);
    }

    return !!options[featureFlag];
  };

  const getFeature = (feature, error) => {
    if (_.isFunction(options[feature])) {
      return options[feature](error);
    }

    return '';
  };
  // An error has extra detail beyond the summary and child messages.
  const errorHasDetail = _.partial(hasFeature, 'hasDetail');
  // Extra detail beyond the summary and child messages. Detail is additional information
  // that is not shown to the user by default and is not part of the message.
  const errorDetail = _.partial(getFeature, 'detail');
  // a help link the user can click on for more info
  const errorHasHelpLink = _.partial(hasFeature, 'hasHelpLink');
  const errorHelpLink = _.partial(getFeature, 'helpLink');
  const errorIsPermament = _.partial(hasFeature, 'isPermanent');

  class CustomError extends Error {
    constructor(detail) {
      super();
      const attrs = { name };
      if (_.isFunction(options.assign)) {
        _.extend(attrs, options.assign(detail));
      }
      _.extend(this, attrs);
      if (!this.errors) {
        _.extend(this, { errors: errorErrors(this) });
      }
      this.message = errorMessage(this);
      this.name = name;

      // Use a try/catch block to capture the stack trace. Capturing the stack trace here is
      // necessary, otherwise we will get the stack trace at the time the new error class was created,
      // rather than when it is instantiated.  We add `message` and `name` so that the stack trace
      // string will match our current error class.
      try {
        throw new Error(this.message);
      } catch (err) {
        err.name = attrs.name;
        this.stack = err.stack;
      }
    }
    static summary(error) {
      return errorSummary(error);
    }
    static hasDetail(error) {
      return errorHasDetail(error);
    }
    static detail(error) {
      return errorDetail(error);
    }
    static errors(error) {
      return errorErrors(error);
    }
    static hasHelpLink(error) {
      return errorHasHelpLink(error);
    }
    static helpLink(error) {
      return errorHelpLink(error);
    }
    static isPermanent(error) {
      return errorIsPermament(error);
    }
    static inspect(error) {
      return error.message
        ? '[' + name + ': ' + error.message + ']'
        : '[' + name + ']';
    }
  }

  return CustomError;
}
